import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, ComposedChart, Line, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import api from '../services/api';
import ClinicalWeekCard from './ClinicalWeekCard';
import {
  MONTH_NAMES, fetchGoalTypes, goalTypeLabel, SEVERITY_LABELS,
  fmtBRL, fmtPct, apiErrorMessage, fetchCompanies, semaforoClass, preferredCompanyId,
} from '../utils/clinicalUtils';
import { exportMonthlyXlsx, exportMonthlyPdf } from '../utils/clinicalExportUtils';
import './clinical.css';

// Cores do semáforo já estabelecidas no projeto (ver clinical.css .badge-*)
const SEMAFORO_HEX = { verde: '#46c46f', amarelo: '#f2b705', vermelho: '#f05252' };
const BRAND_GREEN = '#8ED400';
const BRAND_NAVY = '#C7C9CB'; // prata — segunda série nos gráficos do tema escuro

// ─── Gráfico: Receita por dia da semana ───────────────────────────────────────
// Comparação entre poucas categorias (7 dias) → barra, com tooltip e eixo
// de valores (substitui o antigo "gráfico" feito na unha em CSS puro).
function WeekdayRevenueChart({ weekdays }) {
  const data = weekdays.map((w) => ({
    label: w.weekday_label,
    receita: Number(w.revenue_amount) || 0,
  }));

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2a2c30" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9aa0a6' }} />
        <YAxis
          tick={{ fontSize: 11, fill: '#9aa0a6' }}
          tickFormatter={(v) => fmtBRL(v)}
          width={90}
        />
        <Tooltip formatter={(v) => [fmtBRL(v), 'Receita']} labelStyle={{ fontWeight: 700 }} />
        <Bar dataKey="receita" name="Receita" fill={BRAND_GREEN} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Gráfico: Tendência das semanas do mês (realizado vs. meta) ──────────────
// Indicador que falta pra tomada de decisão: dá pra ver de bate-olho se a
// unidade está performando abaixo/acima da meta ao longo do mês, semana a
// semana — não só o agregado do mês inteiro. Barras (realizado, coloridas
// pelo semáforo da própria semana) + linha (meta), padrão comum pra
// "realizado vs. meta" e mais legível que duas linhas sobrepostas quando há
// só 5 pontos.
function WeeklyTrendChart({ weeks }) {
  const data = weeks.map((w) => ({
    label: `Sem. ${w.week_number}`,
    realizado: Number(w.revenue_amount) || 0,
    meta: w.target_value !== null && w.target_value !== undefined ? Number(w.target_value) : null,
    color: (w.achievement_status && w.achievement_status.color) || null,
  }));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2a2c30" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9aa0a6' }} />
        <YAxis
          tick={{ fontSize: 11, fill: '#9aa0a6' }}
          tickFormatter={(v) => fmtBRL(v)}
          width={90}
        />
        <Tooltip formatter={(v, name) => [fmtBRL(v), name]} labelStyle={{ fontWeight: 700 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="realizado" name="Receita realizada" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => (
            <Cell key={i} fill={SEMAFORO_HEX[d.color] || BRAND_NAVY} />
          ))}
        </Bar>
        <Line
          type="monotone"
          dataKey="meta"
          name="Meta da semana"
          stroke={BRAND_NAVY}
          strokeWidth={2}
          strokeDasharray="6 4"
          dot={{ r: 3 }}
          connectNulls
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/**
 * ClinicalDashboardPage — painel do módulo GRC.
 * KPIs do mês, cards das 5 semanas com cor, gráfico por dia da semana,
 * contadores vs. meta e lista de alertas.
 * Todo o payload vem de GET /clinical-results/dashboard — cálculo 100% backend.
 */
function ClinicalDashboardPage() {
  const now = new Date();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dashboard, setDashboard] = useState(null);
  const [weekdays, setWeekdays] = useState([]);
  const [goalTypeCatalog, setGoalTypeCatalog] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const weeklyChartRef = useRef(null);
  const weekdayChartRef = useRef(null);

  useEffect(() => {
    fetchCompanies()
      .then((list) => {
        setCompanies(list);
        if (list.length && !companyId) setCompanyId(preferredCompanyId(list));
      })
      .catch(() => setError('Falha ao carregar as unidades.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const from = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const [dashRes, weekdayRes, goalTypesRes] = await Promise.all([
        api.get('/clinical-results/dashboard', { params: { company_id: companyId, year, month } }),
        api.get('/clinical-results/by-weekday', { params: { company_id: companyId, from, to } }),
        fetchGoalTypes({ companyId }),
      ]);
      setDashboard(dashRes.data);
      setWeekdays(weekdayRes.data.weekdays || []);
      setGoalTypeCatalog(goalTypesRes);
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao carregar o painel.'));
      setDashboard(null);
      setWeekdays([]);
      setGoalTypeCatalog([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, year, month]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleExportXlsx = () => {
    if (dashboard) {
      exportMonthlyXlsx(dashboard, goalTypeCatalog, {
        weeklyTrendEl: weeklyChartRef.current,
        weekdayEl: weekdayChartRef.current,
      });
    }
  };

  const handleExportPdf = () => {
    if (dashboard) {
      exportMonthlyPdf(dashboard, goalTypeCatalog, {
        weeklyTrendEl: weeklyChartRef.current,
        weekdayEl: weekdayChartRef.current,
      });
    }
  };

  const kpis = dashboard ? dashboard.kpis : null;
  const overallColor = kpis ? kpis.overall_status : null;

  return (
    <div className="clinical-page">
      <h1>Painel — Resultados da Unidade</h1>
      <p className="clinical-subtitle">Semáforo geral segue a pior cor entre conversão, meta e margem.</p>

      {error && <div className="clinical-error">{error}</div>}

      <div className="clinical-filters">
        <label>
          Unidade
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">Selecione...</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <label>
          Mês
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {MONTH_NAMES.map((name, i) => (
              <option key={name} value={i + 1}>{name}</option>
            ))}
          </select>
        </label>
        <label>
          Ano
          <input
            type="number"
            min="2020"
            max="2100"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </label>
        <button className="btn-secondary" onClick={handleExportXlsx} disabled={!dashboard}>Exportar XLSX</button>
        <button className="btn-secondary" onClick={handleExportPdf} disabled={!dashboard}>Exportar PDF</button>
      </div>

      {loading && <p>Carregando...</p>}

      {dashboard && !loading && (
        <>
          <div className="clinical-kpi-grid">
            <div className={`clinical-kpi-card clinical-week-card ${semaforoClass(overallColor)}`}>
              <div className="kpi-label">Semáforo do mês</div>
              <div className="kpi-value">
                <span className={`badge-${overallColor || 'neutro'}`}>
                  {overallColor ? overallColor.toUpperCase() : '—'}
                </span>
              </div>
            </div>
            <div className="clinical-kpi-card">
              <div className="kpi-label">Receita do mês</div>
              <div className="kpi-value">{fmtBRL(kpis.total_revenue)}</div>
            </div>
            <div className="clinical-kpi-card">
              <div className="kpi-label">Lucro líquido</div>
              <div className="kpi-value">{fmtBRL(kpis.net_profit)}</div>
            </div>
            <div className="clinical-kpi-card">
              <div className="kpi-label">Margem líquida</div>
              <div className="kpi-value">{fmtPct(kpis.net_margin)}</div>
            </div>
            <div className="clinical-kpi-card">
              <div className="kpi-label">Conversão em valor</div>
              <div className="kpi-value">{fmtPct(kpis.value_conversion)}</div>
            </div>
            <div className="clinical-kpi-card">
              <div className="kpi-label">Ticket médio</div>
              <div className="kpi-value">{fmtBRL(kpis.average_ticket)}</div>
            </div>
            <div className="clinical-kpi-card">
              <div className="kpi-label">Ticket médio dos orçamentos</div>
              <div className="kpi-value">{fmtBRL(kpis.avg_budget_ticket)}</div>
            </div>
            <div className="clinical-kpi-card">
              <div className="kpi-label">Ticket médio dos orç. fechados</div>
              <div className="kpi-value">{fmtBRL(kpis.avg_closed_budget_ticket)}</div>
            </div>
            <div className="clinical-kpi-card">
              <div className="kpi-label">Atingimento da meta</div>
              <div className="kpi-value">{kpis.achievement !== null ? fmtPct(kpis.achievement) : '—'}</div>
            </div>
          </div>

          <h2 className="clinical-section-title">Semanas do mês</h2>
          <div className="clinical-week-grid">
            {dashboard.weeks.map((week) => (
              <ClinicalWeekCard key={week.week_number} week={week} />
            ))}
          </div>

          <h2 className="clinical-section-title">Tendência das semanas do mês — realizado vs. meta</h2>
          {dashboard.weeks.length === 0 ? (
            <p className="clinical-empty">Sem dados no período.</p>
          ) : (
            <div className="clinical-chart-box" ref={weeklyChartRef}>
              <WeeklyTrendChart weeks={dashboard.weeks} />
            </div>
          )}

          <h2 className="clinical-section-title">Receita por dia da semana</h2>
          {weekdays.length === 0 ? (
            <p className="clinical-empty">Sem dados no período.</p>
          ) : (
            <div className="clinical-chart-box" ref={weekdayChartRef}>
              <WeekdayRevenueChart weekdays={weekdays} />
            </div>
          )}

          <h2 className="clinical-section-title">Contadores de produção vs. meta</h2>
          {dashboard.counters.length === 0 ? (
            <p className="clinical-empty">Nenhum contador lançado no mês.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
            <table className="clinical-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th className="num">Sem. 1</th>
                  <th className="num">Sem. 2</th>
                  <th className="num">Sem. 3</th>
                  <th className="num">Sem. 4</th>
                  <th className="num">Sem. 5</th>
                  <th className="num">Total</th>
                  <th className="num">Meta</th>
                  <th className="num">Atingimento</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.counters.map((c) => {
                  // metas são semanais (RN-30) — a meta/atingimento por semana
                  // já vem calculada em c.weeks; aqui só indexamos por
                  // week_number pra garantir sempre 5 colunas, mesmo que
                  // alguma semana não tenha lançamento.
                  const byWeek = new Map((c.weeks || []).map((w) => [w.week_number, w]));
                  return (
                    <tr key={c.counter_type}>
                      <td>{goalTypeLabel(goalTypeCatalog, c.counter_type)}</td>
                      {[1, 2, 3, 4, 5].map((n) => {
                        const w = byWeek.get(n);
                        return (
                          <td key={n} className="num" title={w && w.target_value !== null ? `Meta da semana: ${w.target_value}` : undefined}>
                            {w ? w.quantity : 0}
                            {w && w.achievement !== null && (
                              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{fmtPct(w.achievement)}</div>
                            )}
                          </td>
                        );
                      })}
                      <td className="num">{c.realized}</td>
                      <td className="num">{c.target_value !== null ? c.target_value : '—'}</td>
                      <td className="num">{c.achievement !== null ? fmtPct(c.achievement) : '—'}</td>
                      <td>
                        {c.status ? (
                          <span className={`badge-${c.status.color}`}>{c.status.label}</span>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}

          <h2 className="clinical-section-title">Alertas</h2>
          <div className="clinical-alerts">
            {dashboard.alerts.length === 0 ? (
              <p className="clinical-empty">Nenhum alerta no período.</p>
            ) : (
              dashboard.alerts.map((a, i) => (
                <div className={`clinical-alert alert-${a.severity}`} key={`${a.rule}-${i}`}>
                  <strong>{a.rule}</strong>
                  <span>[{SEVERITY_LABELS[a.severity] || a.severity}]</span>
                  <span>{a.message}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ClinicalDashboardPage;
